import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { apiDownload, apiFetch } from '../api';
import { Btn, Modal } from '../components';
import { selectAttachments, uploadAttachments } from '../frotaAttachments';
import { loadPendingAttachments, savePendingAttachments } from '../frotaPendingAttachments';

export function AttachmentPicker({ disabled, onSelect }) {
  return <label className="frota-attachment-picker">Anexar comprovante — opcional
    <input aria-label="Anexar comprovante — opcional" type="file" accept=".pdf,.jpg,.jpeg,.png" multiple disabled={disabled} onChange={onSelect} />
    <small>PDF, JPG ou PNG. Até 3 arquivos de 10 MB cada.</small>
  </label>;
}
AttachmentPicker.propTypes = { disabled: PropTypes.bool, onSelect: PropTypes.func.isRequired };

function Receipt({ item, expenseId, available, busy, onDownload }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [viewing, setViewing] = useState(false);
  const isImage = item.tipo === 'image/png' || item.tipo === 'image/jpeg';
  useEffect(() => {
    if (!isImage || !available) return;
    let live = true;
    let objectUrl;
    setError(''); setUrl('');
    apiDownload(`/frota/despesas/${expenseId}/anexos/${item.id}/download`).then(({ blob }) => {
      if (!live) return;
      objectUrl = URL.createObjectURL(blob); setUrl(objectUrl);
    }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [expenseId, item.id, isImage, available, attempt]);
  return <li>
    {isImage && <button type="button" className="frota-receipt-thumbnail" disabled={!url || busy || !available} aria-label={`Visualizar ${item.nome}`} onClick={() => setViewing(true)}>
      {url ? <img src={url} alt={`Miniatura de ${item.nome}`} onError={() => { setError('Não foi possível visualizar esta imagem.'); setUrl(''); }} /> : <span>{error ? 'Sem prévia' : available ? 'Carregando…' : 'Indisponível'}</span>}
    </button>}
    <div className="frota-receipt-info"><strong>{item.nome}</strong><small>{Math.max(1, Math.ceil(item.tamanho / 1024)).toLocaleString('pt-BR')} KB · {new Date(item.criado_em).toLocaleString('pt-BR')}</small>
      {error && <p role="alert">Não foi possível carregar a imagem. <button type="button" disabled={busy || !available} onClick={() => setAttempt(a => a + 1)}>Tentar novamente</button></p>}
      {!isImage && <small>PDF · Baixe o arquivo para abrir no seu leitor.</small>}
    </div>
    <div className="frota-actions">{isImage && <Btn size="sm" variant="secondary" disabled={!url || busy || !available} onClick={() => setViewing(true)}>Visualizar</Btn>}
      <Btn size="sm" variant="secondary" disabled={busy || !available} onClick={() => onDownload(item)}>Baixar</Btn>
    </div>
    {viewing && createPortal(<Modal title="Visualizar comprovante" onClose={() => setViewing(false)}>
      <figure className="frota-receipt-preview"><img src={url} alt={item.nome} /><figcaption>{item.nome}</figcaption></figure>
      <div className="frota-actions"><Btn disabled={busy || !available} onClick={() => onDownload(item)}>Baixar imagem</Btn><Btn variant="secondary" onClick={() => setViewing(false)}>Fechar visualização</Btn></div>
    </Modal>, document.body)}
  </li>;
}
Receipt.propTypes = { item: PropTypes.object.isRequired, expenseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, available: PropTypes.bool, busy: PropTypes.bool, onDownload: PropTypes.func.isRequired };

export default function FrotaAnexos({ expenseId, attachmentScope, cancelled, onBusy, onChanged }) {
  const [data, setData] = useState(null);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [localWarning, setLocalWarning] = useState('');
  const [busy, setBusy] = useState(false);
  const transferring = useRef(false);
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const picker = useRef(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let live = true;
    Promise.all([apiFetch(`/frota/despesas/${expenseId}/anexos`), loadPendingAttachments(attachmentScope, expenseId)]).then(async ([result, recovery]) => {
      if (!live) return;
      // A lost response may already be committed. Reconcile UUIDs before retrying.
      const remaining = recovery.files.filter(entry => !result.items.some(item => item.id === entry.id));
      if (remaining.length !== recovery.files.length) {
        await savePendingAttachments(attachmentScope, expenseId, remaining);
        changed.current();
      }
      if (live) { setData(result); setFiles(remaining); setLocalWarning(recovery.durable ? '' : 'Não foi possível guardar os arquivos neste navegador. Não recarregue a página antes de concluir o envio.'); }
    }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [expenseId, attachmentScope, refresh]);
  const remember = async remaining => {
    setFiles(remaining);
    const persisted = await savePendingAttachments(attachmentScope, expenseId, remaining);
    setLocalWarning(persisted ? '' : 'Não foi possível guardar os arquivos neste navegador. Não recarregue a página antes de concluir o envio.');
    onChanged();
    return persisted;
  };
  const transfer = async fn => {
    if (transferring.current) return;
    transferring.current = true;
    setBusy(true); onBusy(true); setError(''); setMessage('');
    try { await fn(); } catch (e) { setError(e.message); }
    finally { transferring.current = false; setBusy(false); onBusy(false); }
  };
  const upload = () => transfer(async () => {
    let remaining = files;
    try {
      await uploadAttachments(expenseId, files, async entry => {
        remaining = remaining.filter(file => file.id !== entry.id);
        await remember(remaining);
      });
      if (picker.current) picker.current.value = '';
      setMessage('Comprovantes anexados.');
    } finally { setRefresh(r => r + 1); onChanged(); }
  });
  const download = item => transfer(async () => {
    const { blob } = await apiDownload(`/frota/despesas/${expenseId}/anexos/${item.id}/download`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = item.nome; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  const select = e => {
    picker.current = e.target;
    let selected;
    try {
      selected = selectAttachments(e.target.files);
      if (selected.length + data.items.length > 3) throw new Error(`Esta despesa permite mais ${3 - data.items.length} comprovante(s).`);
    } catch (e) {
      picker.current.value = '';
      setFiles([]);
      void transfer(async () => { await remember([]); setError(e.message); });
      return;
    }
    void transfer(async () => { await remember(selected); });
  };
  return <section className="frota-attachments" aria-label="Comprovantes">
    <h3>Comprovantes{data?.items.length ? ` (${data.items.length})` : ''}</h3>
    {error && <div role="alert" className="frota-error">{error} <Btn variant="secondary" size="sm" disabled={busy} onClick={() => { setError(''); setRefresh(r => r + 1); }}>Atualizar anexos</Btn></div>}
    {localWarning && <p role="alert">{localWarning}</p>}
    {message && <p role="status">{message}</p>}
    {!data && !error && <p>Carregando comprovantes...</p>}
    {data && <>
      {!data.items.length && <p>Nenhum comprovante anexado.</p>}
      <ul className="frota-attachment-list">{data.items.map(item => <Receipt key={item.id} item={item} expenseId={expenseId} available={data.disponivel} busy={busy} onDownload={download} />)}</ul>
      {!data.disponivel && <p>Envio e download temporariamente indisponíveis. Os registros da despesa estão preservados.</p>}
      {files.length > 0 && <div className="frota-pending-files" role="status"><strong>{files.length} {files.length === 1 ? 'comprovante com envio pendente' : 'comprovantes com envio pendente'}</strong>
        <p>A despesa foi salva. {localWarning ? 'Conclua o envio antes de recarregar a página.' : 'Os arquivos ficam guardados neste navegador para você retomar o envio.'}</p>
        <ul>{files.map(entry => <li key={entry.id}>{entry.file.name}</li>)}</ul>
        {cancelled && <p>Esta despesa foi cancelada. Os arquivos pendentes não podem mais ser enviados.</p>}
        <div className="frota-actions">{!cancelled && <Btn disabled={busy || !data.disponivel} onClick={upload}>{busy ? 'Enviando...' : 'Enviar comprovantes'}</Btn>}
          <Btn variant="secondary" disabled={busy} onClick={() => void transfer(async () => {
            const discarded = await remember([]);
            if (picker.current) picker.current.value = '';
            if (!discarded) throw new Error('Não foi possível concluir o descarte neste navegador. Tente novamente antes de recarregar.');
            setMessage('Arquivos pendentes descartados. Os comprovantes já anexados foram preservados.');
          })}>Descartar arquivos pendentes</Btn>
        </div>
      </div>}
      {!cancelled && data.disponivel && data.items.length < 3 && <AttachmentPicker disabled={busy} onSelect={select} />}
      {cancelled && <p>Os comprovantes são preservados após o cancelamento.</p>}
    </>}
  </section>;
}
FrotaAnexos.propTypes = { expenseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, attachmentScope: PropTypes.string.isRequired, cancelled: PropTypes.bool, onBusy: PropTypes.func.isRequired, onChanged: PropTypes.func.isRequired };
